/**
 * React context that exposes the cached `GET /api/dashboard/me`
 * response to dashboard children (`DashboardLayout`,
 * `RequireNavSection`, role-scoped pages).
 *
 * Provided by `DashboardLayout` so a single `/me` fetch backs both
 * the role-aware sidebar and the route-level role guards — avoids
 * duplicate fetches and ensures the guard decision uses the same
 * scope payload the sidebar is rendered from.
 *
 * Frontend filtering is UX clarity only — backend route auth
 * remains the security boundary.
 */

import { createContext, useContext, type ReactNode } from 'react';
import type { DashboardMe } from './types';

export interface DashboardMeContextValue {
  readonly me: DashboardMe | null;
  readonly loading: boolean;
  readonly error: string | null;
}

const DashboardMeContext = createContext<DashboardMeContextValue | null>(null);

export function DashboardMeProvider({
  value,
  children,
}: {
  value: DashboardMeContextValue;
  children: ReactNode;
}): JSX.Element {
  return (
    <DashboardMeContext.Provider value={value}>
      {children}
    </DashboardMeContext.Provider>
  );
}

/**
 * Read the dashboard `/me` context. Returns `null` when no provider
 * is present — callers should treat that as a defensive "no access"
 * signal (and typically redirect to `/login`) rather than assume
 * permission.
 */
export function useDashboardMeContext(): DashboardMeContextValue | null {
  return useContext(DashboardMeContext);
}
